import React from 'react';
import styled from 'styled-components';
import {
  spacer8,
  spacer16,
  spacer24,
  spacer40,
  spacer48,
  spacer80,
  screenSmMin,
  screenMdMin,
  screenMdMax,
  screenLgMin,
} from '@spotify-internal/encore-foundation';
import { Type } from '@spotify-internal/encore-web';
import { ModuleType } from '../index';
import { ContentfulImage } from '../../Contentful/ContentfulImage';
import { ContentfulAsset } from '../../Contentful';
import {
  Box,
  ImageAspectBox1x1,
  ButtonTertiaryAsLink,
  withAnimationContext,
  DELAYS,
} from '../../ui';
import {
  withoutProps,
  fixHref,
  formatText,
  transformScaleUp,
} from '../../utils';
import { Colors } from '../HeroNarrativeCarouselModule/Options/colorsType';
import { fadeInAnimation } from '../../ui/Animations';

const HeroBody = styled(Box)`
  background-color: ${(props: Colors) => props.backgroundColor};
  color: ${(props: Colors) => props.textColor};
  display: flex;
  flex-direction: row;
  height: auto;
  padding: ${spacer40} ${spacer48};

  &.tallest {
    height: 100%;
  }

  @media (max-width: ${screenSmMin}) {
    flex-direction: column;
  }
`;

const ImageContainer = styled(ImageAspectBox1x1)`
  width: 30%;
  display: flex;
  align-items: center;
  height: fit-content;
  align-self: center;

  @media (max-width: ${screenMdMax}) {
    width: 40%;
  }

  @media (max-width: ${screenSmMin}) {
    width: 100%;
    margin-bottom: ${spacer8};
  }
`;

const InnerText = styled.div`
  padding-left: ${spacer80};
  width: 50%;
  height: auto;
  max-width: 600px;
  align-self: center;
  margin-top: auto;
  margin-bottom: auto;

  @media (min-width: ${screenLgMin}) {
    max-width: 750px;
    margin-left: 7%;
  }

  @media (max-width: ${screenMdMax}) {
    width: 55%;
  }

  @media (max-width: ${screenSmMin}) {
    width: 100%;
    max-width: none;
    padding-top: ${spacer16};
    padding-left: 0px;
    margin: 0px;
  }
`;

const Headline = withAnimationContext(
  styled(Type.h1).attrs({
    variant: Type.heading1,
    weight: Type.bold,
  })`
    ${fadeInAnimation(DELAYS.FIRST)};
    padding-bottom: ${spacer16};
    max-width: 684px;

    @media (min-width: ${screenMdMin}) {
      text-align: left;
    }
  `,
);

const BodyText = withAnimationContext(
  styled(Type.h4).attrs({
    variant: Type.heading4,
  })`
    ${fadeInAnimation(DELAYS.SECOND)};
    padding-bottom: ${spacer24};

    @media (min-width: ${screenMdMin}) {
      padding-top: ${spacer16};
    }

    @media (max-width: ${screenSmMin}) {
      padding-bottom: ${spacer8};
    }
  `,
);

const CTABtn = styled(
  withoutProps(ButtonTertiaryAsLink, 'textColor', 'backgroundColor'),
)`
  padding-top: ${spacer16};
  background-color: ${(props: Colors) => props.textColor};
  color: ${(props: Colors) => props.backgroundColor};

  @media (max-width: ${screenSmMin}) {
    padding-right: ${spacer8};
    padding-top: ${spacer16};
    padding-left: ${spacer8};
    width: 100%;
    justify-content: center;
  }

  &:hover:not(:focus):not(:disabled) {
    color: ${(props: Colors) => props.backgroundColor};
    ${transformScaleUp};
  }
`;

const CTAContainer = withAnimationContext(
  styled.div`
    ${fadeInAnimation(DELAYS.THIRD)};
    display: flex;
    padding-top: ${spacer16};
    padding-bottom: ${spacer24};

    @media (max-width: ${screenSmMin}) {
      width: 100%;
      justify-content: center;
    }
  `,
);

type HeroBodyModuleType = ModuleType & {
  headline?: string;
  bodyText?: string;
  image?: ContentfulAsset;
  ctaLink?: string;
  ctaText?: string;
  backgroundColor?: string;
  textColor?: string;
};

const TRACK_CAT = 'module-heroBody';

export const HeroBodyModule = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  HeroBodyModuleType
>(
  (
    { headline, bodyText, image, ctaLink, ctaText, backgroundColor, textColor },
    ref,
  ) => {
    return (
      <HeroBody
        backgroundColor={backgroundColor}
        textColor={textColor}
        ref={ref as React.RefObject<HTMLDivElement>}
      >
        {image && (
          <ImageContainer>
            <ContentfulImage asset={image} />
          </ImageContainer>
        )}

        <InnerText>
          {headline && (
            <Headline
              data-testid="hero-body-headline"
              dangerouslySetInnerHTML={{ __html: formatText(headline) }}
            />
          )}
          {bodyText && (
            <BodyText
              dangerouslySetInnerHTML={{ __html: formatText(bodyText) }}
            />
          )}
          {ctaText && ctaLink && (
            <CTAContainer>
              <CTABtn
                textColor={textColor}
                href={fixHref(ctaLink)}
                track={[TRACK_CAT, `CTA Link - ${ctaText}`]}
                backgroundColor={backgroundColor}
              >
                {ctaText}
              </CTABtn>
            </CTAContainer>
          )}
        </InnerText>
      </HeroBody>
    );
  },
);
